import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Box, Divider, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import ScreenContainer from "../../Components/ScreenContainer";
import MultiSelectDropdown from "../../PagesContent/Overview/components/MultiSelectDropdown";
import SeverityStatistics from "../../PagesContent/BmsFaults/SeverityStatistics";
import AlertListItem from "../../PagesContent/BmsFaults/AlertListItem";
import AlertListHeader from "../../PagesContent/BmsFaults/AlertListHeader";
import { Pagination } from "antd";
import { getDiagnosticsBatteriesList } from "../../Api/BmsFaultsApi";
import { getDigitalTwinList } from "../../Api/DigitalTwinListApi";
import CartLoader from "../../Helper/Loaders/CartLoader";
import AlertActionDialog from "../../PagesContent/BmsFaults/AlertActionDialog";
import {
  convertZonetoUTCStart,
  convertZonetoUTCEnd,
  analyticsCalendarEndDate,
} from "../../Helper/DatePicker/DateConverter";
import SelectionClearWarningDialog from "../../PagesContent/BmsFaults/Components/SelectionClearWarningDialog";
import { getConnectedBatteries, getFilterData, getPagesMetaData } from "../../Api/Api";
import OperationalStatusMultiSelect from "../../PagesContent/Common/OperationalStatusMultiSelect";
import AlertSystemTabs from "../../PagesContent/BmsFaults/AlertSystemTabs";
import Events from "../../Analytics/Events";
import CODE from "../../Static/Constants/StatusCodes";
import SingleSelectDropdown from "../../PagesContent/Overview/components/SingleSelectDropdown";
import BmsFaultsAnalytics from "../../PagesContent/BmsFaults/Analytics/BmsFaultsAnalytics";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import OnboardingLocationFilter from "../../PagesContent/Overview/components/OnboardingLocationFilter";
import AppCache from "../../Helper/Common/AppCache";
import { getDateRangeFromTimePeriod } from "../../PagesContent/BmsFaults/CommonFunctions";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";
import { isFilterVisible } from "../../Helper/Common/pageFilterVisibility";

const tabs = [
  {
    slug: "active",
    name: "Active",
    color: ThemeProperties.purple,
  },
  {
    slug: "resolved",
    name: "Resolved",
    color: ThemeProperties.purple,
  },
  {
    slug: "analytics",
    name: "Analytics",
    color: ThemeProperties.purple,
  },
];

const timePeriodOptions = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 30 days",
  "Last 45 days",
  "Last 90 days",
];

const BmsFaults = () => {
  const queryParams = decryptTheParams();
  const defaultTabSlug = queryParams.tab
    ? JSON.parse(queryParams.tab)
    : tabs[0]?.slug;
  const {
    allFleets,
    selectedFleets,
    allOnboardingLocations,
    allOnboardingLocationsHierarchy,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
    handleFleetAndLocationChange,
    canUseGlobalAdvancedFilter,
    filtersLoading,
  } = useGlobalFilterData();

  // BmsFaults original state
  const [pagesContent, setPagesContent] = useState({ mounted: false });
  const [selectedTab, setSelectedTab] = useState(defaultTabSlug);
  const [alertCategories, setAlertCategories] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    defaultTabSlug === "resolved" ? timePeriodOptions[0] : null
  );
  const [filtersFromStats, setFiltersFromStats] = useState({});
  const [allDefaultFiltersApplied, setAllDefaultFiltersApplied] =
    useState(false);
  const [allGlobalFiltersLoaded, setAllGlobalFiltersLoaded] = useState(false);

  // AlertList state (merged from AlertList component)
  const [expandAll, setExpandAll] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isManualSelectAll, setIsManualSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBatteries, setSelectedBatteries] = useState(new Set());
  const [selectedAlertIncidents, setSelectedAlertIncidents] = useState(
    new Set()
  );
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [actionDialogData, setActionDialogData] = useState({});
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [batteryList, setBatteryList] = useState([]);
  const [paginationData, setPaginationData] = useState({
    rowsPerPage: 10,
    currentPage: 1,
    total: 1,
  });
  const [searchTerm, setSearchTerm] = useState({
    selectedDevices: [],
    selectedSearchColumn: null,
  });
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedSort, setSelectedSort] = useState([]);
  const [defaultSort, setDefaultSort] = useState([]);

  // Cache for fetched incidents (keyed by batteryID) to avoid redundant API calls
  const incidentsCacheRef = useRef({});
  // Tracks the latest fetchBatteries request to discard stale responses
  const fetchRequestRef = useRef(0);

  const globalFilterConfig = AppCache.getItem("globalFilter")
    ?.find((f) => f.type === "unifiedGlobalFilter") || null;

  // Analytics tab state
  const [showChartDetails, setShowChartDetails] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [selectedChartGranularity, setSelectedChartGranularity] = useState(null);
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() => {
    const today = new Date();
    const endDate = analyticsCalendarEndDate();

    const startDateObj = new Date();
    startDateObj.setDate(today.getDate() - 7);
    const startDate = startDateObj.toISOString().split('T')[0];

    return {
      startDate,
      endDate,
    };
  });
  const [vehiclesConnected, setVehiclesConnected] = useState(0);
  const [isLoadingVehiclesCount, setIsLoadingVehiclesCount] = useState(false);

  const fetchAlertCategories = async () => {
    const filterConfig = {
      type: "dynamic",
      queryParams: { table: "diagnosticsMetaBOP", column: "category" },
    };
    const res = await getFilterData(filterConfig);
    const alertCategoryList = res?.response?.response?.filterData || [];
    const alertCategoryListValues = alertCategoryList.map(
      (category) => category.id
    );
    setAlertCategories(alertCategoryListValues);
  };

  const handleAlertCategorySelection = (selectedOptions) => {
    confirmSelectionClear(() => {
      setGlobalFilters(prev => ({
        ...prev,
        alertCategories: selectedOptions,
      }));
    });
  };

  const handleGlobalFilterChange = (change) => {
    confirmSelectionClear(() => handleFleetAndLocationChange(change));
  };

  const handleDepotSelection = (selectedOptions) => {
    confirmSelectionClear(() => {
      setGlobalFilters(prev => ({
        ...prev,
        onboardingLocations: { ...prev.onboardingLocations, depots: selectedOptions },
      }));
    });
  };

  const handleOperationalStatusSelection = (selectedValues) => {
    confirmSelectionClear(() => {
      setGlobalFilters(prev => ({
        ...prev,
        operationalStatus: selectedValues,
      }));
    });
  };

  const handleTimePeriodSelection = (selectedOptions) => {
    confirmSelectionClear(() => {
      setSelectedTimePeriod(selectedOptions);
    });
  };

  const handleFiltersFromStats = (filtersToApply) => {
    // setFiltersFromStats(filtersToApply);
  };

  const fetchVehiclesConnected = useCallback(async () => {
    setIsLoadingVehiclesCount(true);
    try {
      const response = await getConnectedBatteries(globalFilters, 'bmsFaults');

      if (response?.responseStatus?.code === CODE.SUCCESS) {
        setVehiclesConnected(response?.response?.data?.[0]?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching vehicles connected:', error);
      setVehiclesConnected(0);
    } finally {
      setIsLoadingVehiclesCount(false);
    }
  }, [globalFilters]);

  // AlertList functions (merged from AlertList component)
  const handleSearch = (selectedDevices, selectedSearchColumn) => {
    confirmSelectionClear(() => {
      setSearchTerm({
        selectedDevices: selectedDevices,
        selectedSearchColumn: selectedSearchColumn,
      });
      setPaginationData((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    });
  };

  const handleFilters = (filters) => {
    confirmSelectionClear(() => {
      setSelectedFilters(filters);
      setPaginationData((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    });
  };

  const handleExpandToggle = (updatedValue) => {
    setExpandAll(updatedValue);
  };

  const handleSelectAllToggle = (checked) => {
    setIsManualSelectAll(true);
    setSelectAll(checked);

    if (checked) {
      const allBatteryIDs = new Set(batteryList.map((b) => b.batteryID));
      const allIncidents = new Set(
        batteryList.flatMap((b) =>
          (b.alertIncidents || []).map((i) => i.incidentID)
        )
      );

      setSelectedBatteries(allBatteryIDs);
      setSelectedAlertIncidents(allIncidents);
    } else {
      setSelectedBatteries(new Set());
      setSelectedAlertIncidents(new Set());
    }
  };

  const handleSelect = useCallback((record, selected) => {
    setSelectedBatteries(prev => {
      const batterySet = new Set(prev);
      if (selected) {
        batterySet.add(record.batteryID);
      } else {
        batterySet.delete(record.batteryID);
      }
      return batterySet;
    });
  }, []);

  const handleSortChange = (updatedSort) => {
    confirmSelectionClear(() => {
      setSelectedSort([
        {
          sortBy: updatedSort.key,
          order: updatedSort.order,
        },
      ]);
      setPaginationData((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    });
  };

  const handlePageChange = (page) => {
    confirmSelectionClear(() => {
      setPaginationData((prev) => ({
        ...prev,
        currentPage: page,
      }));
    });
  };

  const getDateRangeFromTimePeriod = (timePeriod) => {
    // Return null if timePeriod is not provided
    if (!timePeriod) {
      return null;
    }

    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (timePeriod) {
      case "Today":
        startDate.setDate(now.getDate());
        endDate.setDate(now.getDate());
        break;
      case "Yesterday":
        startDate.setDate(now.getDate() - 1);
        endDate.setDate(now.getDate() - 1);
        break;
      case "Last 7 days":
        startDate.setDate(now.getDate() - 7);
        break;
      case "Last 30 days":
        startDate.setDate(now.getDate() - 30);
        break;
      case "Last 45 days":
        startDate.setDate(now.getDate() - 45);
        break;
      case "Last 90 days":
        startDate.setDate(now.getDate() - 90);
        break;
      default:
        // Return null for unrecognized time periods
        return null;
    }

    // Format dates as "YYYY-MM-DD" for timezone conversion
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Convert user timezone dates to UTC with proper start/end of day handling
    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    return {
      resolvedAt: JSON.stringify({
        startDateUTC: convertZonetoUTCStart(startDateStr),
        endDateUTC: convertZonetoUTCEnd(endDateStr),
      }),
    };
  };

  const fetchBatteries = async () => {
    // wait for all default filters to be applied
    if (!allDefaultFiltersApplied) return false;

    const requestId = ++fetchRequestRef.current;
    setIsLoading(true);
    const searchID = searchTerm.selectedSearchColumn || "searchID";
    const dateRange = selectedTab === "resolved" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;

    const filters = {
      ...(dateRange && dateRange),
      [searchID]: searchTerm.selectedDevices,
      pageCount: paginationData.rowsPerPage,
      pageNum: paginationData.currentPage,
      sortBy: JSON.stringify(selectedSort),
      errorStatus: [selectedTab === "resolved" ? "resolved" : "active"],
      ...selectedFilters,
    };

    const batteriesRes = await getDiagnosticsBatteriesList(
      globalFilters,
      filters
    );

    // Discard response if a newer request has been issued (e.g. tab switched mid-flight)
    if (requestId !== fetchRequestRef.current) return;

    const batteriesData = batteriesRes?.response?.data || [];
    setBatteryList(batteriesData);

    const batteriesTotals = batteriesRes?.response?.total || {};
    setPaginationData((prev) => ({
      ...prev,
      total: batteriesTotals.total,
    }));

    setIsLoading(false);
  };

  // Memoized incident filters for lazy loading in AlertListItem
  const incidentFilters = useMemo(() => {
    const dateRange = getDateRangeFromTimePeriod(selectedTimePeriod);
    const incidentsSortBy = selectedSort.map((sort) => {
      if (sort.sortBy === "batteriesBySeverity") {
        return {
          ...sort,
          sortBy: "severityLevelID",
        };
      }
      return sort;
    });
    return {
      errorStatus: selectedTab === "resolved" ? "resolved" : "active",
      ...(dateRange && dateRange),
      ...selectedFilters,
      sortBy: JSON.stringify(incidentsSortBy),
      skipPagination: true,
    };
  }, [selectedTab, selectedTimePeriod, selectedFilters, selectedSort]);

  const handleActionClick = useCallback((battery, alertIncident) => {
    setActionDialogData({ battery, alertIncident });
    setOpenActionDialog(true);
  }, []);

  // Callback for AlertListItem to update the incidents cache when fetched
  const handleIncidentsFetched = useCallback((batteryID, incidents) => {
    incidentsCacheRef.current[batteryID] = incidents;
  }, []);

  const confirmSelectionClear = (action) => {
    if (selectedBatteries.size || selectAll) {
      setPendingAction(() => action);
      setIsWarningOpen(true);
    } else {
      action();
    }
  };

  const clearSelection = () => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
    setIsManualSelectAll(false);
    setSelectAll(false);
    setPendingAction(null);
  };

  async function getPageMeta() {
    const pageName = "bmsAlertV2";
    const res = await getPagesMetaData(pageName);
    Events("bmsAlertV2 page opened");

    if (res && res?.responseStatus?.code === CODE.SUCCESS) {
      const pageData = res.response?.response?.metaInfo;
      setPagesContent({
        ...pagesContent,
        metaInfo: pageData,
        mounted: true,
      });
      return;
    }
    // Mark it settled even on failure, matching PlatformAlerts. The analytics
    // tables wait for this before fetching, so leaving it false on an error path
    // would hang them rather than fall back to the built-in column config.
    setPagesContent({ ...pagesContent, mounted: true });
  }

  React.useEffect(() => {
    getPageMeta();
  }, []);

  useEffect(() => {
    fetchFleets();
    fetchAlertCategories();
  }, []);

  useEffect(() => {
    if (selectedFleets.length > 0) {
      fetchOnboardingLocations(selectedFleets);
    }
  }, [selectedFleets]);

  // Per-page filter visibility, the same block Battery Intelligence reads for
  // SOHAndRUL. See isFilterVisible for why an absent key means shown here.
  const pageFiltersConfig = pagesContent?.metaInfo?.globalFilters;
  const showFilter = (key) => isFilterVisible(pageFiltersConfig, key);

  // The org meta arrives after the first render, so the control mounts, seeds a
  // default, and only then gets hidden. Clear what it seeded: a hidden filter
  // that still scopes the request is one the user cannot see or undo, and an org
  // hides it precisely because operational status means nothing to them.
  useEffect(() => {
    if (showFilter("operationalStatus")) return;
    setGlobalFilters((prev) =>
      (prev?.operationalStatus || []).length
        ? { ...prev, operationalStatus: [] }
        : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFiltersConfig]);

  useEffect(() => {
    // The operational status control is the only thing that seeds this value, so
    // an org that hides it through org meta would wait on something nothing can
    // produce and the list would never load. Wait for it only while it is shown.
    const operationalStatusReady =
      !showFilter("operationalStatus") ||
      (globalFilters?.operationalStatus || []).length > 0;
    const hasAllFilters =
      (globalFilters?.fleets || []).length > 0 &&
      (Object.keys(globalFilters?.onboardingLocations || {})).length > 0 &&
      operationalStatusReady;
    setAllGlobalFiltersLoaded(hasAllFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilters, pageFiltersConfig]);


  useEffect(() => {
    const defaultFilters = Object.keys(
      queryParams.filters ? JSON.parse(queryParams.filters) : {}
    ).length
      ? JSON.parse(queryParams.filters)
      : filtersFromStats;

    // Separate originDepo from other filters
    const { originDepo, ...otherFilters } = defaultFilters;

    // Store originDepo as pending filter to be applied when onboarding locations are fetched
    if (originDepo && Array.isArray(originDepo) && originDepo.length > 0) {
      if (allOnboardingLocations?.depots?.length > 0) {
        setGlobalFilters((prev) => ({
          ...prev,
          onboardingLocations: {
            ...prev.onboardingLocations,
            depots: originDepo,
          },
        }));
      }
      setAllDefaultFiltersApplied(true);
    } else {
      setAllDefaultFiltersApplied(true);
    }

    // change only if exact match to avoid API calls
    if (JSON.stringify(selectedFilters) !== JSON.stringify(otherFilters)) {
      setSelectedFilters(otherFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.filters, filtersFromStats, allOnboardingLocations]);

  useEffect(() => {
    const defaultPageSort = [
      {
        sortBy: "batteriesBySeverity",
        order: "desc",
      },
    ];

    if (queryParams?.sort) {
      setDefaultSort(JSON.parse(queryParams.sort));
      setSelectedSort(JSON.parse(queryParams.sort));
    } else {
      setDefaultSort(defaultPageSort);
      setSelectedSort(defaultPageSort);
    }
  }, [queryParams.sort]);

  useEffect(() => {
    if (allGlobalFiltersLoaded && Object.keys(globalFilters).length > 0) {
      fetchBatteries();
    }
  }, [
    globalFilters,
    paginationData?.currentPage,
    selectedFilters,
    selectedSort,
    searchTerm,
    selectedTimePeriod,
    selectedTab,
    allDefaultFiltersApplied,
    allGlobalFiltersLoaded
  ]);

  useEffect(() => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
  }, [selectedTab]);

  // Clear incidents cache when filters change
  useEffect(() => {
    incidentsCacheRef.current = {};
  }, [globalFilters, incidentFilters]);

  useEffect(() => {
    if (batteryList.length === 0) return;
    const shouldSelectAll = selectedBatteries.size === batteryList.length;
    // Prevent re-triggering child select resets when this is just auto-sync
    if (!isManualSelectAll) {
      setSelectAll(shouldSelectAll);
    }
    // Once this runs, reset manual flag
    if (isManualSelectAll) {
      setIsManualSelectAll(false);
    }
  }, [selectedBatteries, batteryList]);

  const handleShowChartDetails = (show, chartType, granularity) => {
    setShowChartDetails(show);
    setSelectedChartType(chartType);
    setSelectedChartGranularity(granularity);
  };

  const handleGlobalFiltersChange = (updatedFilters) => {
    setGlobalFilters(prev => ({
      ...prev,
      ...updatedFilters
    }));
  };

  // Fetch vehicles connected count when Analytics tab is active
  useEffect(() => {
    if (selectedTab === "analytics" && Object.keys(globalFilters).length > 0) {
      fetchVehiclesConnected();
    }
  }, [selectedTab, globalFilters, analyticsDateRange, fetchVehiclesConnected]);

  return (
    <ScreenContainer pagesContent={pagesContent}>
      {!showChartDetails && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ px: 2, display: "flex" }}>
            <Typography variant="fw700s20lh24c2E2E2E">
              Battery System Faults
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: 1,
              justifyContent: "space-between",
              borderBottom: "1px solid #E4E3E3",
              mt: 0,
              pr: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AlertSystemTabs
                tabs={tabs}
                selectedTab={selectedTab}
                changeTheTab={(tabId) => {
                  confirmSelectionClear(() => {
                    setSelectedTab(tabId);
                    if (tabId === "resolved" && !selectedTimePeriod) {
                      setSelectedTimePeriod(timePeriodOptions[0]);
                    }
                    setPaginationData((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                  });
                }}
              />
            </Box>
            <FilterBar
              testId="bms-faults-filters"
              filters={[
                selectedTab === "resolved" && showFilter("timePeriod") && {
                  key: "timePeriod",
                  node: (
                    <SingleSelectDropdown
                      selectedOption={selectedTimePeriod}
                      options={timePeriodOptions}
                      label="Time Period"
                      onChange={handleTimePeriodSelection}
                    />
                  ),
                },
                showFilter("alertCategories") && {
                  key: "alertCategories",
                  node: (
                    <MultiSelectDropdown
                      selectedOptions={globalFilters?.alertCategories ?? []}
                      options={alertCategories}
                      label="Alert Categories"
                      onChange={handleAlertCategorySelection}
                      defaultAllSelected={true}
                    />
                  ),
                },
                showFilter("operationalStatus") && {
                  key: "operationalStatus",
                  node: (
                    <OperationalStatusMultiSelect
                      selectedValues={globalFilters?.operationalStatus || []}
                      onSelectionChange={handleOperationalStatusSelection}
                      onOptionsLoaded={(allValueCodes, defaultSelection) => setGlobalFilters(prev => ({
                        ...prev,
                        operationalStatus: defaultSelection,
                        allOperationalStatus: allValueCodes,
                      }))}
                    />
                  ),
                },
                showFilter("location") && {
                  key: "customerLocation",
                  primary: true,
                  node: canUseGlobalAdvancedFilter && globalFilterConfig ? (
                    <GlobalFilter
                      isLoading={filtersLoading}
                      allFleets={allFleets}
                      allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                      selectedFleetIds={globalFilters?.fleets || []}
                      selectedLocations={globalFilters?.hierarchicalLocation}
                      onChange={handleGlobalFilterChange}
                    />
                  ) : selectedTab === "analytics" ? (
                    <OnboardingLocationFilter
                      locations={allOnboardingLocationsHierarchy}
                      selectedLocations={globalFilters?.hierarchicalLocation}
                      onChange={(selected) => setGlobalFilters(prev => ({ ...prev, hierarchicalLocation: selected }))}
                    />
                  ) : (
                    <MultiSelectDropdown
                      selectedOptions={globalFilters?.onboardingLocations?.depots || []}
                      options={allOnboardingLocations?.depots || []}
                      label="Operating Location"
                      onChange={handleDepotSelection}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Box>
      )}

      {/* page wrapper */}
      <Box sx={{ px: 3, py: 2, mb: 2 }}>
        <Grid2 container gap={2} flexDirection={"column"}>
          <>
            {/* Gated on the same readiness flag the list tab uses. globalFilters
                starts empty and gains fleets only after the fleet and location
                requests resolve, so rendering this eagerly fetched every chart
                two or three times per load - the first calls unscoped, and their
                rows visible. allGlobalFiltersLoaded rather than fleets.length
                because it already waits on operational status only while that
                filter is shown. */}
            {selectedTab === "analytics" && allGlobalFiltersLoaded ? (
              <BmsFaultsAnalytics
                allFleets={allFleets}
                allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                startDate={analyticsDateRange.startDate}
                endDate={analyticsDateRange.endDate}
                onDateChange={({ startDate, endDate }) => {
                  setAnalyticsDateRange({ startDate, endDate });
                }}
                vehiclesConnected={vehiclesConnected}
                isLoadingVehiclesCount={isLoadingVehiclesCount}
                showChartDetails={showChartDetails}
                selectedChartType={selectedChartType}
                selectedChartGranularity={selectedChartGranularity}
                onShowChartDetails={handleShowChartDetails}
                globalFilters={globalFilters}
                onGlobalFiltersChange={handleGlobalFiltersChange}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {selectedTab === "active" && (
                  <>
                    <SeverityStatistics
                      globalFilters={globalFilters}
                      defaultFiltersApplied={allDefaultFiltersApplied}
                      globalFiltersLoaded={allGlobalFiltersLoaded}
                      applyFiltersFromStats={handleFiltersFromStats}
                      autoApplyFirstFilter={true}
                    />
                    <Divider
                      orientation="horizontal"
                      flexItem
                      sx={{ borderColor: "#E4E4E4", mx: "-24px" }}
                    />
                  </>
                )}
                {/* AlertList content (merged inline) */}
                <Grid2 container flexDirection={"column"} gap={2} mb={4}>
                  <AlertListHeader
                    onExpandToggle={handleExpandToggle}
                    onSelectAllToggle={handleSelectAllToggle}
                    selectAll={selectAll}
                    resultCount={paginationData.total}
                    onSearch={handleSearch}
                    selectedFilters={selectedFilters}
                    onApplyFilters={handleFilters}
                    defaultSort={defaultSort}
                    onSortChange={handleSortChange}
                    selectedTab={selectedTab}
                    selectedBatteries={selectedBatteries}
                    selectedAlertIncidents={selectedAlertIncidents}
                    searchTerm={searchTerm}
                    globalFilters={globalFilters}
                    selectedTimePeriod={selectedTimePeriod}
                    incidentsCache={incidentsCacheRef.current}
                    incidentFilters={incidentFilters}
                  />
                  {isLoading || !allGlobalFiltersLoaded || !allDefaultFiltersApplied ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 5.25,
                        bgcolor: "#F3F5F7",
                      }}
                    >
                      <CartLoader />
                    </Box>
                  ) : (
                    <>
                      {batteryList.length ? (
                        batteryList.map((battery) => {
                          return (
                            <AlertListItem
                              key={battery.batteryID}
                              battery={battery}
                              expandAll={expandAll}
                              selectedTab={selectedTab}
                              selectedBatteries={selectedBatteries}
                              onSelect={handleSelect}
                              onActionClick={handleActionClick}
                              globalFilters={globalFilters}
                              incidentFilters={incidentFilters}
                              onIncidentsFetched={handleIncidentsFetched}
                            />
                          );
                        })
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 4,
                            bgcolor: "#F3F5F7",
                          }}
                        >
                          <Typography
                            variant="fw500s12c686868"
                            sx={{
                              fontWeight: 600,
                              fontSize: "18px",
                              lineHeight: "20px",
                            }}
                          >
                            No Vehicles found for filters applied
                          </Typography>
                        </Box>
                      )}
                      {batteryList.length > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "right" }}>
                          <Pagination
                            showSizeChanger={false}
                            onShowSizeChange={(current, size) => { }}
                            current={paginationData.currentPage}
                            pageSize={paginationData.rowsPerPage}
                            total={paginationData?.total}
                            onChange={(page) => handlePageChange(page)}
                            showTotal={(total, range) =>
                              `${range[0]}-${range[1]} / ${total}`
                            }
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Grid2>
                <AlertActionDialog
                  actionData={actionDialogData}
                  open={openActionDialog}
                  onClose={(hasUpdatedData) => {
                    setOpenActionDialog(false);
                    // Reload data only if changes were saved
                    if (hasUpdatedData) {
                      fetchBatteries();
                    }
                  }}
                />
                <SelectionClearWarningDialog
                  open={isWarningOpen}
                  onProceed={() => {
                    if (pendingAction) {
                      pendingAction();
                      setPendingAction(null);
                    }
                    setIsWarningOpen(false);
                    clearSelection();
                  }}
                  onCancel={() => {
                    setPendingAction(null);
                    setIsWarningOpen(false);
                  }}
                />
              </Box>
            )}
          </>
        </Grid2>
      </Box>
    </ScreenContainer>
  );
};

export default BmsFaults;
